"use client"

import Link from "next/link"
import { config } from "@/config"
import { ArrowRight, Brain, FileJson, Sparkles, Zap } from "lucide-react"
import { motion } from "motion/react"

import { useSmoothScroll } from "@/hooks/use-smooth-scroll"
import { Badge } from "@/components/ui/badge"
import { buttonVariants } from "@/components/ui/button"

const HERO_STATS = [
  { value: "5", label: "Formats supported", icon: FileJson },
  { value: "Real-time", label: "Token preview", icon: Zap },
  { value: "GPT-4", label: "Accurate encoding", icon: Brain },
]

export function HeroSection() {
  const handleSmoothScroll = useSmoothScroll()

  return (
    <section className="from-background via-primary/5 to-background relative overflow-hidden border-b border-white/10 bg-linear-to-b py-20 sm:py-28 dark:border-white/5">
      <div className="absolute inset-0">
        <div className="bg-grid-white/10 dark:bg-grid-white/[0.06] absolute inset-0 bg-size-[20px_20px]" />
        <div className="from-background via-primary/10 pointer-events-none absolute right-0 bottom-0 left-0 h-48 bg-linear-to-t to-transparent" />
      </div>
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-0">
        <div className="mx-auto flex max-w-4xl flex-col items-center space-y-8 text-center">
          <Badge className="border-zinc-300 bg-white/95 px-4 py-1.5 text-xs font-semibold tracking-[0.2em] text-zinc-900 backdrop-blur dark:border-zinc-700 dark:bg-zinc-800/95 dark:text-zinc-100">
            <Sparkles className="mr-2 h-3.5 w-3.5" />
            Token intelligence for builders
          </Badge>
          <h1 className="dark:text-primary-foreground text-4xl font-semibold tracking-tight text-balance text-zinc-900 md:text-5xl lg:text-6xl">
            {config.name} -
            <span className="from-primary bg-linear-to-r to-sky-400 bg-clip-text text-transparent">
              {" "}
              {config.slogan}
            </span>
          </h1>
          <p className="text-muted-foreground max-w-2xl text-base leading-relaxed md:text-lg">
            {config.description}
          </p>
          <div className="flex flex-col items-center gap-3 pt-2 sm:flex-row sm:gap-4">
            <Link
              href="/analyze"
              className={buttonVariants({
                variant: "default",
                size: "lg",
                className:
                  "shadow-primary/30 hover:shadow-primary/40 w-full shadow-lg sm:w-auto",
              })}
            >
              Analyze my JSON
              <ArrowRight className="h-4 w-4" />
            </Link>
            <motion.button
              type="button"
              whileHover={{ y: -2, opacity: 0.95 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => handleSmoothScroll("features")}
              aria-label="Explore features"
              className={buttonVariants({
                variant: "outline",
                size: "lg",
                className:
                  "w-full border-zinc-300 bg-white/90 text-zinc-900 backdrop-blur hover:bg-white sm:w-auto dark:border-zinc-700 dark:bg-zinc-800/90 dark:text-zinc-100 dark:hover:bg-zinc-800",
              })}
            >
              Explore features
            </motion.button>
          </div>
          <div className="text-muted-foreground flex flex-col items-center gap-3 text-sm sm:flex-row">
            <div className="border-muted bg-muted flex h-9 items-center rounded-full border px-4 backdrop-blur dark:border-white/20 dark:bg-white/20">
              <div className="mr-2 flex h-2 w-2 animate-pulse rounded-full bg-emerald-500" />
              <span className="text-foreground dark:text-foreground">
                Live GPT-4 tokenization
              </span>
            </div>
            <span className="text-primary-foreground/70 hidden text-xs tracking-[0.4em] uppercase sm:inline">
              •
            </span>
            <p className="text-muted-foreground text-xs tracking-[0.2em] uppercase">
              Built for production datasets
            </p>
          </div>
          <div className="grid w-full max-w-2xl grid-cols-1 gap-4 pt-10 sm:grid-cols-3">
            {HERO_STATS.map((stat) => {
              const Icon = stat.icon
              return (
                <div
                  key={stat.label}
                  className="group relative overflow-hidden rounded-3xl border border-zinc-200 bg-linear-to-br from-white to-zinc-50 px-6 py-6 text-center shadow-xl ring-1 ring-black/5 transition-all hover:-translate-y-0.5 hover:shadow-2xl dark:border-zinc-800 dark:from-zinc-900 dark:to-zinc-950 dark:ring-white/10"
                >
                  <div className="pointer-events-none absolute inset-x-6 -top-10 h-20 rounded-full bg-white/40 opacity-0 blur-3xl transition group-hover:opacity-70 dark:bg-white/10" />
                  <div className="text-primary mb-3 flex justify-center">
                    <Icon className="h-6 w-6 transition-transform group-hover:scale-110" />
                  </div>
                  <p className="group-hover:text-primary text-3xl font-semibold text-zinc-900 transition dark:text-zinc-100">
                    {stat.value}
                  </p>
                  <p className="text-xs tracking-wide text-zinc-600 uppercase dark:text-zinc-400">
                    {stat.label}
                  </p>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </section>
  )
}
